// Split-dropdown for the SQL formatter. The button opens a small menu:
//   • Full format (reflow)   — multi-line clause layout
//   • Single line             — collapse the statement onto one line
//   • Indent routine body     — re-indent a BEGIN…END / IF / loop body
//   • Change case only        — uppercase keywords, nothing else touched
// Below a separator it also hosts the editor view toggles (auto-format on “;”,
// wrap long lines) — these used to be standalone toolbar buttons but they're
// sticky prefs, not per-run actions, so they live in the menu now. A toggle
// item is only rendered when its handler prop is supplied (QueryPanel passes
// both; ScriptEditor only wrap). Toggling keeps the menu open so you can see the
// check flip and flick both. The format actions honor a selection (handled by
// formatEditor on the view). Ctrl+Shift+F = full format. Reuses ExportMenu's
// dropdown styling.
//
// Props:
//   onPick(mode)         fn   — 'full' | 'oneline' | 'indent' | 'case'
//   disabled             bool
//   autoFormat           bool — current auto-format state (only with onToggleAutoFormat)
//   onToggleAutoFormat   fn   — flip auto-format; omit to hide that toggle
//   lineWrap             bool — current line-wrap state (only with onToggleLineWrap)
//   onToggleLineWrap     fn   — flip line-wrap; omit to hide that toggle
import { createSignal, Show } from 'solid-js';
import { useDismissable } from '../lib/dismissable.js';

export default function FormatMenu(props) {
  const [open, setOpen] = createSignal(false);
  let btnRef, menuRef;
  useDismissable(() => [btnRef, menuRef], () => setOpen(false), { event: 'click' });

  const pick = (mode) => { setOpen(false); props.onPick?.(mode); }; // actions close the menu

  return (
    <div class="export-menu">
      <button ref={btnRef} class="btn btn-sm btn-ghost" disabled={props.disabled}
              onClick={() => setOpen(!open())}
              title="Format SQL — full reflow or change-case-only (Ctrl+Shift+F = full). Also holds the auto-format / wrap toggles.">
        ⌗ Format ▾
      </button>
      <Show when={open()}>
        <div ref={menuRef} class="export-menu-pop">
          <button class="export-menu-item" onClick={() => pick('full')}>Full format (reflow)</button>
          <button class="export-menu-item" onClick={() => pick('oneline')}>Single line</button>
          <button class="export-menu-item" onClick={() => pick('indent')}
                  title="Re-indent a stored-routine body (BEGIN…END / IF / loops) — Shift+Alt+I">Indent routine body</button>
          <button class="export-menu-item" onClick={() => pick('case')}>Change case only</button>

          <Show when={props.onToggleAutoFormat || props.onToggleLineWrap}>
            <div class="export-menu-sep" />
          </Show>
          <Show when={props.onToggleAutoFormat}>
            <button class="export-menu-item export-menu-toggle" onClick={() => props.onToggleAutoFormat()}
                    title="Reformat each plain statement when you type its “;” (DELIMITER / routine scripts are skipped).">
              <span class="export-menu-check">{props.autoFormat ? '✓' : ''}</span>
              Auto-format on “;”
            </button>
          </Show>
          <Show when={props.onToggleLineWrap}>
            <button class="export-menu-item export-menu-toggle" onClick={() => props.onToggleLineWrap()}
                    title="Wrap long lines instead of scrolling horizontally.">
              <span class="export-menu-check">{props.lineWrap ? '✓' : ''}</span>
              Wrap long lines
            </button>
          </Show>
        </div>
      </Show>
    </div>
  );
}
